import React, { useEffect, useMemo, useState } from 'react';
import { SCRUBBER_API_BASE } from '../api/config';
import { csrfFetch } from '../api/csrf';

/**
 * Submit as Final — the destination is a deliberate choice, made here.
 *
 * The file was scrubbed in the Scrubber, the table identified at upload
 * (OD, RECON, PYMT_C…), and the period declared. On submit the user states
 * what this final universe is FOR:
 *
 *   · Monitoring — the existing compliance-review path (COA scoring).
 *   · An audit  — the ORIGINAL uploaded file (every row, exact bytes),
 *     every error by type and volume (row/field/category only, never a
 *     message, never PHI), the period, and the timeliness are filed onto
 *     the matching universe slot of an open internal audit in Core. Core
 *     verifies the table against the slot for itself and refuses a
 *     mismatch. Universe integrity testing in the audit remains the
 *     auditor's own manual, question-by-question act.
 *
 * Zero retention holds: the file goes straight from this screen's memory;
 * nothing lands on Scrubber disk.
 */

// Scrubber universe type → audit slot code. Explicit and exact: a substring
// match once preselected Table 1's slot for every ODAG table. Mirrors
// SCRUBBER_TYPE_TO_CODE in Core, which enforces the same map on receipt.
const TYPE_TO_CODE = {
  odag_table1: 'OD',
  odag_table2_reconsiderations: 'RECON',
  odag_table3_pymt_c: 'PYMT_C',
  odag_table4_eff_c: 'EFF_C',
  odag_table5_grv_c: 'GRV_C',
  cdag_table1_cd: 'CD',
  cdag_table2_cder: 'CDER',
  cdag_table3_pymt_d: 'PYMT_D',
  cdag_table4_rd: 'RD',
  cdag_table5_eff_d: 'EFF_D',
  cdag_table6_grv_d: 'GRV_D',
  cdag_table7_ar: 'AR',
  snpcc_snpe: 'SNPE',
  snpe: 'SNPE',
};

export default function SubmitFinalDialog({
  open, onClose, totalIssues,
  universe, uploadedFile, totalRows, errors, timeliness, timelinessSummary, selectedTable, universePeriod,
  onSubmitMonitoring,
}) {
  const [dest, setDest] = useState('monitoring');
  const [audits, setAudits] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [slotId, setSlotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);

  const universeType = String((selectedTable && selectedTable.universeType) || '').toLowerCase();
  const myCode = TYPE_TO_CODE[universeType] || null;

  useEffect(() => {
    if (!open) return;
    setDest('monitoring'); setSlotId(''); setNote(''); setDone(false); setBusy(false);
    csrfFetch(`${SCRUBBER_API_BASE}/api/audit-slots`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setAudits((d && d.audits) || []); setOrgName((d && d.organization) || null); })
      .catch(() => setAudits([]));
  }, [open]);

  const options = useMemo(() => {
    const out = [];
    for (const a of audits || []) {
      for (const s of a.slots || []) {
        out.push({ id: s.id, code: s.code, status: s.status, label: `${(a.program || '').toUpperCase()} ${a.cycle_label} — ${s.label}` });
      }
    }
    return out;
  }, [audits]);

  // Only the slots for THIS table are offered; the identification is the
  // Scrubber's, made at upload. When the type is unknown to the map, every
  // slot is offered and Core still verifies on receipt.
  const offered = useMemo(() => (myCode ? options.filter((o) => o.code === myCode) : options), [options, myCode]);

  useEffect(() => {
    if (!open || slotId || !offered.length) return;
    if (myCode) setSlotId(offered[0].id);
  }, [open, offered, myCode, slotId]);

  if (!open) return null;
  const rows = Array.isArray(universe) ? universe : [];
  const hasAudits = !!(audits && offered.length);

  const confirm = async () => {
    setBusy(true); setNote('');
    try {
      if (dest === 'monitoring') {
        await onSubmitMonitoring();
        onClose();
        return;
      }
      if (!slotId) { setNote('Pick the audit universe slot first.'); setBusy(false); return; }
      const cleanErrors = (errors || []).map((e) => ({ row: e && e.row, field: e && e.field, category: (e && (e.category || e.type)) || 'other' }));
      const payload = {
        universe_request_id: slotId,
        errors: cleanErrors,
        timeliness: timeliness || null,
        timeliness_summary: timelinessSummary || null,
        selectedTable: selectedTable || {},
        universePeriod: universePeriod || null,
        row_count: totalRows || rows.length || 0,
        filename: universeType || 'universe',
      };
      let r;
      if (uploadedFile) {
        // The original file, exact bytes, every row.
        const fd = new FormData();
        fd.append('file', uploadedFile, uploadedFile.name);
        fd.append('payload', JSON.stringify(payload));
        r = await csrfFetch(`${SCRUBBER_API_BASE}/api/file-to-audit`, { method: 'POST', body: fd });
      } else {
        if (!rows.length) { setNote('The universe file is not on this screen; run the validation again and submit from the results.'); setBusy(false); return; }
        r = await csrfFetch(`${SCRUBBER_API_BASE}/api/file-to-audit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, universe: rows }),
        });
      }
      const d = await r.json().catch(() => null);
      if (!r.ok || !d || d.success !== true) throw new Error((d && d.message) || `Filing failed (${r.status})`);
      setDone(true);
      const f = d.filed || {};
      setNote(`Filed into the audit: ${f.slot}. ${f.rows != null ? `${Number(f.rows).toLocaleString()} rows` : 'the universe file'}, ${f.errors} errors recorded by type, validation report saved. Universe integrity testing in the audit remains the auditor's own review.`);
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 className="text-base font-bold text-slate-800">Submit as final</h3>
          <p className="text-xs text-slate-500 mt-1">
            {selectedTable && selectedTable.label ? `${selectedTable.label}${myCode ? ` (${myCode})` : ''}. ` : ''}
            {totalRows ? `${Number(totalRows).toLocaleString()} rows. ` : ''}
            {totalIssues > 0 ? `${totalIssues} validation error${totalIssues === 1 ? '' : 's'}. ` : 'No validation errors. '}
            Choose what this final universe is for.
          </p>
        </div>

        <div className="space-y-2">
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${dest === 'monitoring' ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200'}`}>
            <input type="radio" name="submit-dest" className="mt-0.5" checked={dest === 'monitoring'} onChange={() => setDest('monitoring')} disabled={busy || done} />
            <span>
              <span className="block text-sm font-semibold text-slate-800">Monitoring</span>
              <span className="block text-xs text-slate-500">Goes to compliance review and can score the oversight calendar.</span>
            </span>
          </label>
          <label className={`flex items-start gap-3 p-3 rounded-xl border ${hasAudits ? 'cursor-pointer' : 'opacity-50'} ${dest === 'audit' ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200'}`}>
            <input type="radio" name="submit-dest" className="mt-0.5" checked={dest === 'audit'} onChange={() => setDest('audit')} disabled={busy || done || !hasAudits} />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-slate-800">An open audit{orgName ? <span className="font-normal text-slate-500"> · {orgName}</span> : null}</span>
              <span className="block text-xs text-slate-500">Files the whole universe file, every error by type and volume, and the timeliness onto the audit's {myCode ? `${myCode} ` : ''}universe slot. No messages, no PHI in the error record.</span>
              {!hasAudits && <span className="block text-xs text-slate-400 mt-1">{audits === null ? 'Checking for open audits…' : (myCode ? `No open audit is awaiting a ${myCode} universe.` : 'No open audits with universe slots.')}</span>}
              {dest === 'audit' && hasAudits && (
                <select value={slotId} onChange={(e) => setSlotId(e.target.value)} disabled={busy || done}
                  className="mt-2 w-full text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white text-slate-700">
                  <option value="">Pick the audit…</option>
                  {offered.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}{o.status === 'received' ? ' (already received)' : ''}</option>
                  ))}
                </select>
              )}
            </span>
          </label>
        </div>

        {note && <p className={`text-xs ${done ? 'text-emerald-700' : 'text-rose-700'}`}>{note}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button onClick={confirm} disabled={busy || (dest === 'audit' && !slotId)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-rose-500 to-orange-600 hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold">
              {busy ? 'Submitting…' : dest === 'audit' ? 'File into the audit' : 'Submit for review'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
